import { useSimpleShoppingCart } from "@with-nx/ecommerce";
import {
  AnalyticsHelper,
  Currency,
  requestMicroserviceDropsSSR,
  requestShowBrandOnlyShowSSR,
  requestVariantSSR,
  useDropCollections,
  usePricing,
} from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import { DesignedCard, ReusableSection, Rule } from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  CatalogLayoutWait,
  ReusableSimpleHeroSection,
  SearchEmpty,
} from "@with-nx/simple-ui/organisms";
import { ShowDataStatic } from "@with-nx/static";
import { Col, Row } from "antd";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { ShowsTab } from "../../../../../components/w/shows/ShowsTab";

const RedirectCartModal = dynamic(() =>
  import(
    "../../../../../components/w/redirect-cart-modal/redirect-cart-modal"
  ).then((c) => c.RedirectCartModal)
);

const DigitalDropPopModal = dynamic(
  () =>
    import(
      "../../../../../components/w/digital-drop-pop-modal/digital-drop-pop-modal"
    )
);

export const getServerSideProps = async (context) => {
  const { resolvedUrl } = context;
  const { variant } = context.params;

  const showVariant = !ShowDataStatic.isShowsEmpty()
    ? ShowDataStatic.getShowBySlug(variant) ||
      (await requestVariantSSR(variant))
    : await requestVariantSSR(variant);

  const [drops, showOnly] = await (async () => {
    try {
      const [drops, showOnly] = await Promise.all([
        requestMicroserviceDropsSSR(showVariant?.showBrand?.slug),
        requestShowBrandOnlyShowSSR(showVariant?.showBrand?.slug),
      ]);

      return [drops, showOnly];
    } catch {
      return [[], { only: true }];
    }
  })();

  return {
    props: {
      resolvedUrl,
      showVariant,
      drops,
      showOnly,
    },
  };
};

export default function Page({ resolvedUrl, showVariant, showOnly, drops }) {
  const router = useRouter();

  const region = useServerRegion();
  const basket = useSimpleShoppingCart();
  const pricing = usePricing({
    type: "DIGITAL_DROP",
    region: region?.id,
  });

  const collections = useDropCollections();
  const [modal, _modal] = useState(false);
  const [productTitle, _productTitle] = useState("");
  const [popModal, _popModal] = useState(false);
  const [selectedIndex, _selectedIndex] = useState(0);
  const selectedDrop = drops?.[selectedIndex];

  const getPreviewImage = (item) =>
    item.attachments?.find((i) => i.name === "preview_still")?.blob?.signedUrl;

  const getPreviewVideo = (item) =>
    item.attachments?.find((i) => i.name === "preview_video")?.blob?.signedUrl;

  const handleAddToCart = (item) => {
    basket.add(basket.transform(item)?.product?.id, "DIGITAL_DROP");
  };

  useEffect(() => {
    AnalyticsHelper.collect(showVariant?.id);
  }, [showVariant?.id]);

  if (!showVariant?.id) {
    return;
  }

  let shouldHideLogo =
    !showVariant?.scenicProjections.length && !showVariant?.choreoGuide;

  if (showVariant?.forceShowLogo) {
    shouldHideLogo = false;
  }

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={
        showVariant?.metaTitle
          ? showVariant?.metaTitle
          : showVariant?.title
          ? `${showVariant?.title} Digital Backdrops for Theatre | Broadway Media`
          : undefined
      }
      description={
        showVariant?.metaDescription
          ? showVariant?.metaDescription
          : showVariant?.marketingDescription
      }
      resolvedUrl={resolvedUrl}
    >
      <CatalogLayoutWait until={[true]}>
        <ReusableSimpleHeroSection
          image={
            showVariant?.attachments?.find((i) => i.name === "image")?.blob
              .signedUrl
          }
          press={() => {
            router.push(`/shows/${router.query?.brand}`);
          }}
          title={showVariant?.title}
          description={showVariant?.marketingDescription}
          show={{
            name: showVariant?.title,
            image: shouldHideLogo
              ? undefined
              : showVariant?.attachments?.find((i) => i.name === "logo")?.blob
                  .signedUrl,
            color: showVariant?.logoColor || showVariant?.showBrand?.logoColor,
          }}
        />

        <div className="container">
          <ShowsTab
            waitlist={showVariant?.scenicProjections?.length === 0}
            empty={false}
            loading={false}
            showVariant={showVariant}
            onlyShow={showOnly.only}
            currentTab="Backdrop Library"
          />
        </div>

        {drops?.length ? (
          <ReusableSection>
            <div className="container">
              <Row gutter={[36, 18]}>
                <Col xs={24}>
                  <Row justify="center">
                    <Col xs={24} sm={24} md={22} lg={20}>
                      <Rule
                        rule="dm"
                        align="center"
                        parse="!hl ta:center"
                        color="var(--font1)"
                        bottom={12}
                        display="block"
                      >
                        Suggested Backdrops for {showVariant?.title}
                      </Rule>
                      <Rule
                        rule="hl"
                        align="center"
                        parse="!hl ta:center"
                        color="var(--font1)"
                        bottom={24}
                        display="block"
                      >
                        Supplement or customize your projection package with
                        additional options from our vast digital backdrop
                        library. Choose additional backdrops for your package to
                        match your creative vision.
                      </Rule>
                      <Rule
                        rule="pl"
                        align="center"
                        parse="!pl ta:center"
                        color="var(--font1)"
                        bottom={24}
                        display="block"
                      >
                        Prefer to view the official script-accurate show
                        package?{" "}
                        <a
                          href={`/shows/${router.query?.brand}/${router.query?.variant}/scenic-projections`}
                          style={{ color: "var(--primary)" }}
                        >
                          View the Scenic Projections here
                        </a>
                        .
                      </Rule>
                    </Col>
                  </Row>
                </Col>
                {drops?.map((item, index) => (
                  <Col xs={24} sm={24} md={12} lg={6} key={item.id}>
                    <DesignedCard
                      size="16x9"
                      video={
                        item?.attachments?.find(
                          (i) => i.name === "preview_video"
                        )?.blob?.signedUrl
                      }
                      image={
                        item?.attachments?.find(
                          (i) => i.name === "preview_still"
                        )?.blob?.signedUrl
                      }
                      pop={true}
                      renderPop={() => {
                        _selectedIndex(index);
                        _popModal(true);
                      }}
                      inside={{
                        cart: {
                          added: basket.added(item.product?.id),
                          press: () => {
                            if (!basket.authenticated) {
                              return;
                            }
                            basket.add(item.product?.id, "DIGITAL_DROP");

                            _productTitle(`Digital Backdrop "${item?.name}"`);
                            _modal(true);
                          },
                          authenticated: basket.authenticated,
                          properties: {
                            product: item.product?.id?.toString(),
                            type: "DIGITAL_DROP",
                          },
                        },
                        tagPosition: "bottom-left",
                      }}
                      outside={{
                        subtitle: item.id.toString(),
                        title: (() => {
                          try {
                            return item?.name?.split("(")[0].trim();
                          } catch (error) {
                            return item.name;
                          }
                        })(),
                        price: {
                          price: Currency.format(
                            parseFloat(pricing?.price) || 0,
                            region.currency.code
                          ),
                        },
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          </ReusableSection>
        ) : (
          <ReusableSection>
            <div className="container">
              <SearchEmpty />
            </div>
          </ReusableSection>
        )}

        {modal && (
          <RedirectCartModal
            modal={modal}
            _modal={_modal}
            title={productTitle}
          />
        )}

        {popModal && selectedDrop && (
          <DigitalDropPopModal
            popModal={popModal}
            _popModal={_popModal}
            selectedDrop={selectedDrop}
            basket={basket}
            image={getPreviewImage(selectedDrop)}
            video={getPreviewVideo(selectedDrop)}
            onAddToCart={handleAddToCart}
            onGoNext={() => {
              if (selectedIndex < drops?.length) {
                _selectedIndex(selectedIndex + 1);
              }
            }}
            onGoPrevious={() => {
              if (selectedIndex > 0) {
                _selectedIndex(selectedIndex - 1);
              }
            }}
            disablePrevious={selectedIndex === 0}
            disableNext={selectedIndex === drops?.length - 1}
          />
        )}
      </CatalogLayoutWait>
    </CatalogLayout>
  );
}
